import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import useTrialExpirationHandler from './useTrialExpirationHandler';
import { useSubscriptionExpirationHandler } from './useSubscriptionExpirationHandler';
import { logger } from '../utils/logger';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export interface Idea {
  id: string;
  createdAt: string;
  account: string;
  sector: string;
  platform: string;
  topic: string;
  isProcessing?: boolean;
  icp: {
    id: string;
    title: string;
    data: string;
  };
  projectedImpact: {
    level: string;
    color: string;
    score: number;
  };
  user?: {
    firstName?: string;
    lastName?: string;
    email?: string;
  };
}

export interface PaginationInfo {
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export interface FilterConfig {
  platform?: string;
  account?: string;
  sector?: string;
  topic?: string;
  icp?: string;
  projectedImpact?: string;
  createdAfter?: string;
  createdBefore?: string;
  createdBy?: string;
}

export interface SortConfig {
  key: string;
  direction: 'asc' | 'desc';
}

export interface UseIdeasReturn {
  // Data
  ideas: Idea[];
  loading: boolean;
  error: string | null;
  totalCount: number;
  
  // Pagination
  page: number;
  rowsPerPage: number;
  
  // Sorting
  sortConfig: SortConfig;
  
  // Filtering
  filterConfig: FilterConfig;
  showFilters: boolean;
  availablePlatforms: string[];
  availableSectors: string[];
  availableImpactLevels: string[];
  
  // Organization info
  orgInfo: any;
  showOnboarding: boolean;
  
  // Trial/Subscription modals
  isTrialExpiredModalOpen: boolean;
  isTrialAdmin: boolean;
  closeTrialExpiredModal: () => void;
  isSubscriptionExpiredModalOpen: boolean;
  isSubscriptionAdmin: boolean;
  closeSubscriptionExpiredModal: () => void;
  
  // Actions
  setPage: (page: number) => void;
  setRowsPerPage: (rowsPerPage: number) => void;
  setSortConfig: (config: SortConfig | ((prev: SortConfig) => SortConfig)) => void;
  setFilterConfig: (config: FilterConfig | ((prev: FilterConfig) => FilterConfig)) => void;
  setShowFilters: (show: boolean) => void;
  handleSort: (key: string) => void;
  handleFilterChange: (key: keyof FilterConfig, value: any) => void;
  clearFilters: () => void;
  fetchIdeas: () => void;
  handleChangePage: (event: unknown, newPage: number) => void;
  handleChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export const useIdeas = (): UseIdeasReturn => {
  // State
  const [ideas, setIdeas] = useState<Idea[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'createdAt', direction: 'desc' });
  const [filterConfig, setFilterConfig] = useState<FilterConfig>({});
  const [showFilters, setShowFilters] = useState(false);
  const [availablePlatforms, setAvailablePlatforms] = useState<string[]>([
    'Twitter/X', 'Facebook', 'Instagram', 'LinkedIn', 'TikTok'
  ]);
  const [availableSectors, setAvailableSectors] = useState<string[]>([
    'Technology', 'Healthcare', 'Finance', 'Retail', 'Entertainment', 'Education', 'Automotive'
  ]);
  const [availableImpactLevels, setAvailableImpactLevels] = useState<string[]>([
    'Strong Uplift', 'Mild Uplift', 'Neutral', 'Mild Damage', 'Severe Damage'
  ]);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [orgInfo, setOrgInfo] = useState<any>(null);
  
  // Hooks
  const { user } = useAuth();
  
  // Trial expiration handling
  const { 
    isExpiredModalOpen: isTrialExpiredModalOpen, 
    isAdmin: isTrialAdmin, 
    handleApiError: handleTrialApiError, 
    closeExpiredModal: closeTrialExpiredModal 
  } = useTrialExpirationHandler();

  // Subscription expiration handling
  const { 
    isExpiredModalOpen: isSubscriptionExpiredModalOpen, 
    isAdmin: isSubscriptionAdmin, 
    handleApiError: handleSubscriptionApiError, 
    closeExpiredModal: closeSubscriptionExpiredModal 
  } = useSubscriptionExpirationHandler();

  // Check if user needs onboarding (no organization assigned)
  useEffect(() => {
    // Removed duplicate onboarding trigger - handled centrally in AuthContext
    // if (user && !user.organizationId) {
    //   setShowOnboarding(true);
    // } else if (user && user.organizationId) {
    if (user && user.organizationId) {
      // Fetch organization info
      fetchOrgInfo();
    }
  }, [user]);

  // Fetch organization info
  const fetchOrgInfo = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/organization`);
      setOrgInfo(response.data.organization);
    } catch (error) {
      // Check if trial has expired or subscription is cancelled
      if (!handleTrialApiError(error)) {
        handleSubscriptionApiError(error);
      }
      logger.error('Error fetching organization info:', error);
    }
  };

  // Helper function to get impact color
  const getImpactColor = (level: string) => {
    switch (level) {
      case 'Strong Uplift': return '#06D6A0';
      case 'Mild Uplift': return '#80D6A0';
      case 'Neutral': return '#FFD166';
      case 'Mild Damage': return '#FF9A66';
      case 'Severe Damage': return '#FF6B6B';
      default: return '#9CA3AF';
    }
  };

  const fetchIdeas = useCallback(async (pageNum = 1, limit = 10, opts?: { silent?: boolean }) => {
    try {
      if (!opts?.silent) setLoading(true);
      
      // Build params from current state
      const params: any = {
        page: pageNum,
        limit: limit,
        sortBy: sortConfig.key,
        sortOrder: sortConfig.direction,
        ...filterConfig
      };
      
      // Make actual API call to get ideas
      const response = await axios.get(`${API_URL}/api/ideas`, { params });
      
      // Map the backend response to match the frontend interface
      const mappedIdeas: Idea[] = response.data.ideas.map((idea: any) => {
        // Use the bestDraft info that backend already calculated
        const bestDraft = idea.bestDraft || { uiScore: 0, level: 'Unknown' };
        const inProgress = (idea._count?.drafts ?? 0) === 0 && (!idea.generationMetadata || idea.generationMetadata === null);
        
        return {
          id: idea.id,
          createdAt: idea.createdAt,
          account: idea.brand, // Backend uses 'brand', frontend expects 'account'
          sector: idea.sector,
          platform: idea.platform,
          topic: idea.topic,
          isProcessing: inProgress,
          icp: idea.icp,
          projectedImpact: {
            level: bestDraft.level,
            color: getImpactColor(bestDraft.level),
            score: bestDraft.uiScore
          },
          user: idea.user
        };
      });
      
      setIdeas(mappedIdeas);
      
      // Set total count from pagination data
      if (response.data.pagination) {
        setTotalCount(response.data.pagination.total);
      } else {
        setTotalCount(mappedIdeas.length);
      }
    } catch (err) {
      // Check if trial has expired or subscription is cancelled
      if (!handleTrialApiError(err) && !handleSubscriptionApiError(err)) {
        setError('Failed to load ideas. Please try again later.');
        logger.error('Error fetching ideas:', err);
      }
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }, [sortConfig, filterConfig, handleTrialApiError, handleSubscriptionApiError]);

  // Fetch ideas when dependencies change
  useEffect(() => {
    fetchIdeas(page + 1, rowsPerPage);
  }, [page, rowsPerPage, fetchIdeas]);

  // Silent polling while any idea is processing
  useEffect(() => {
    const hasInProgress = ideas.some((i) => i.isProcessing || (i.projectedImpact.level === 'Unknown' && i.projectedImpact.score <= 0));
    if (!hasInProgress) return;
    const interval = setInterval(() => {
      fetchIdeas(page + 1, rowsPerPage, { silent: true });
    }, 6000);
    return () => clearInterval(interval);
  }, [ideas, page, rowsPerPage, fetchIdeas]);

  // Handler functions
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  const handleSort = (key: string) => {
    setSortConfig(prevConfig => {
      if (prevConfig.key === key) {
        // Toggle direction if already sorting by this key
        return { 
          key, 
          direction: prevConfig.direction === 'asc' ? 'desc' : 'asc' 
        };
      }
      // Default to descending when sorting by a new key
      return { key, direction: 'desc' };
    });
  };

  const handleFilterChange = (key: keyof FilterConfig, value: any) => {
    setFilterConfig(prev => ({
      ...prev,
      [key]: value
    }));
    setPage(0); // Reset to first page when filters change
  };
  
  const clearFilters = () => {
    setFilterConfig({});
    setPage(0);
  };

  const refetchIdeas = () => {
    fetchIdeas(page + 1, rowsPerPage);
  };

  return {
    // Data
    ideas,
    loading,
    error,
    totalCount,
    
    // Pagination
    page,
    rowsPerPage,
    
    // Sorting
    sortConfig,
    
    // Filtering
    filterConfig,
    showFilters,
    availablePlatforms,
    availableSectors,
    availableImpactLevels,
    
    // Organization info
    orgInfo,
    showOnboarding,
    
    // Trial/Subscription modals
    isTrialExpiredModalOpen,
    isTrialAdmin,
    closeTrialExpiredModal,
    isSubscriptionExpiredModalOpen,
    isSubscriptionAdmin,
    closeSubscriptionExpiredModal,
    
    // Actions
    setPage,
    setRowsPerPage,
    setSortConfig,
    setFilterConfig,
    setShowFilters,
    handleSort,
    handleFilterChange,
    clearFilters,
    fetchIdeas: refetchIdeas,
    handleChangePage,
    handleChangeRowsPerPage,
  };
};
